import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { RosterMutationsApi } from '../api/rosterMutations';
import type { BrandMutationsApi } from '../api/brandMutations';
import { campaignCopy, errorMessage } from '../../copy';
import { campaignKeys } from '../queryKeys';
import {
  applyOptimisticDeliverableSelection,
  rollbackCreatorsPages,
  upsertCreatorAcrossPills,
  removeCreatorFromAllPills,
  type CreatorsCacheSnapshot,
} from '../rosterOptimisticCache';

export function useBrandCampaignMutations(
  id: string,
  rosterApi: RosterMutationsApi,
  brandApi: BrandMutationsApi,
) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(id) });
  };

  const approveScript = useMutation({
    mutationFn: ({ creatorId }: { creatorId: string }) => brandApi.approveScript(creatorId),
    onSuccess: () => { invalidate(); toast.success(campaignCopy.script.approve.success); },
    onError: (err: Error) => toast.error(errorMessage(err, campaignCopy.script.approve.error)),
  });

  const approveContent = useMutation({
    mutationFn: (updates: { creator_id: string }[]) =>
      brandApi.approveContent(updates.map((u) => u.creator_id)),
    onSuccess: () => { invalidate(); toast.success(campaignCopy.content.approve.success); },
    onError: (err: Error) => toast.error(errorMessage(err, campaignCopy.content.approve.error)),
  });

  const approveCreator = useMutation({
    mutationFn: ({ creatorId, deliverables }: { creatorId: string; amount?: number; deliverables?: string[] }) =>
      rosterApi.approveCreator(creatorId, deliverables ? { selected_deliverables: deliverables } : undefined),
    onSuccess: (_data, { creatorId }) => {
      // No invalidate() here on purpose: the BE fans this transition out
      // async (accepted_by_brand can chain further server-side before the
      // HTTP response), so an immediate refetch here can race that chain and
      // overwrite the correct optimistic move with stale intermediate data —
      // producing a flash-back-then-move-again a few seconds later. The
      // optimistic cache patch is the source of truth until the
      // `creator_accepted_by_brand` SSE event (useCampaignSSE) confirms the
      // BE-side transition actually settled and does the real refetch.
      upsertCreatorAcrossPills(queryClient, id, creatorId, 'accepted_by_brand');
      toast.success(campaignCopy.creator.approve.success);
    },
    onError: (err: Error) => toast.error(errorMessage(err, campaignCopy.creator.approve.error)),
  });

  const rejectCreator = useMutation({
    mutationFn: ({ creatorId, reason }: { creatorId: string; reason: string }) =>
      rosterApi.rejectCreator(creatorId, reason),
    onSuccess: (_data, { creatorId }) => {
      // A rejected row leaves the Creators funnel entirely — evict it from
      // every cached pill instantly instead of waiting on the background
      // invalidate() refetch. Same no-invalidate() rationale as approveCreator
      // above — `creator_rejected` SSE does the authoritative refetch.
      removeCreatorFromAllPills(queryClient, id, creatorId);
      toast.success(campaignCopy.creator.reject.success);
    },
    onError: (err: Error) => toast.error(errorMessage(err, campaignCopy.creator.reject.error)),
  });

  const postScriptThreadComment = useMutation({
    mutationFn: ({ creatorId, body }: { creatorId: string; body: string }) =>
      rosterApi.postScriptThreadComment(creatorId, body),
    onSuccess: (_, vars) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(vars.creatorId, 'script') });
    },
    onError: (err: Error) => toast.error(errorMessage(err, campaignCopy.thread.sendMessage.error)),
  });

  const postContentThreadComment = useMutation({
    mutationFn: ({ creatorId, body }: { creatorId: string; body: string }) =>
      rosterApi.postContentThreadComment(creatorId, body),
    onSuccess: (_, vars) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(vars.creatorId, 'content') });
    },
    onError: (err: Error) => toast.error(errorMessage(err, campaignCopy.thread.sendMessage.error)),
  });

  const selectDeliverable = useMutation({
    mutationFn: ({
      rosterEntryId,
      types,
    }: {
      rosterEntryId: string;
      types: string[];
    }) => rosterApi.selectDeliverable(rosterEntryId, types),

    onMutate: async ({ rosterEntryId, types }) => {
      // Stop any refetch that could overwrite the optimistic data.
      await queryClient.cancelQueries({ queryKey: campaignKeys.rosterTab(id, 'creators') });
      // Patch budget + reach + cpv across every cached Creators page and
      // snapshot for rollback (fixes the prior exact-key phantom write).
      const previous = applyOptimisticDeliverableSelection(
        queryClient,
        id,
        rosterEntryId,
        types,
      );
      return { previous };
    },

    onError: (_err, _vars, context?: { previous: CreatorsCacheSnapshot }) => {
      if (context?.previous) rollbackCreatorsPages(queryClient, context.previous);
    },

    // onSettled fires after both success AND error — guaranteed cache consistency.
    onSettled: () => {
      invalidate(); // rosterAll prefix — refetches row data with server truth
      queryClient.invalidateQueries({ queryKey: campaignKeys.rosterKpisCreators(id) });
    },
  });

  const submitEditRequest = useMutation({
    mutationFn: (payload: Parameters<BrandMutationsApi['submitEditRequest']>[1]) =>
      brandApi.submitEditRequest(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.editRequestCurrent(id) });
      toast.success('Changes submitted for review');
    },
    onError: (err: Error) => toast.error(errorMessage(err, 'Failed to submit changes')),
  });

  return {
    approveScript,
    approveContent,
    approveCreator,
    rejectCreator,
    postScriptThreadComment,
    postContentThreadComment,
    selectDeliverable,
    submitEditRequest,
  };
}
